import React, { useEffect, useState } from 'react';
import api from '../api';

// A coach's invoicing details: the address their invoice is sent FROM, and the
// account the club pays them into.
//
// ONLY THE COACH EVER SEES THIS. There is no admin version of this card and no
// admin route behind it. A club officer who could change the account a coach
// is paid into would be the single most dangerous button in the portal, so the
// ability does not exist rather than being hidden.
//
// Split into two independent forms on purpose. The address needs nothing
// special and always works. The bank details need an encryption key to be
// configured on the server, and when it is not they are refused outright —
// nothing is ever written in the clear. A coach who only wants their invoice
// addressed properly is not blocked by the other half being unavailable.

const BLANK = { account_name: '', sort_code: '', account_number: '' };

const BillingDetails = () => {
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState('');
  const [savedAddress, setSavedAddress] = useState('');
  const [bank, setBank] = useState(null);          // what the server holds
  const [form, setForm] = useState(BLANK);         // what is being typed
  const [editingBank, setEditingBank] = useState(false);
  const [available, setAvailable] = useState(true);
  const [unavailableReason, setUnavailableReason] = useState('');
  const [addressMsg, setAddressMsg] = useState('');
  const [bankMsg, setBankMsg] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await api.get('/api/profile/billing');
      setAddress(res.data.billing_address || '');
      setSavedAddress(res.data.billing_address || '');
      setBank(res.data.bank);
      setAvailable(res.data.bank_storage_available);
      setUnavailableReason(res.data.bank_storage_problem || '');
    } catch {
      setError('Your billing details could not be loaded.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveAddress = async (e) => {
    e.preventDefault();
    setAddressMsg(''); setError('');
    try {
      const res = await api.put('/api/profile/billing-address', { billing_address: address });
      setSavedAddress(res.data.billing_address || '');
      setAddressMsg('Address saved. It will appear on your next invoice.');
    } catch (err) {
      setError(err.response?.data?.error || 'That address could not be saved.');
    }
  };

  const saveBank = async (e) => {
    e.preventDefault();
    setBankMsg(''); setError('');
    try {
      const res = await api.put('/api/profile/bank-details', form);
      setBank(res.data);
      setForm(BLANK);
      setEditingBank(false);
      setBankMsg('Bank details saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Those bank details could not be saved.');
    }
  };

  const removeBank = async () => {
    if (!window.confirm('Remove your bank details? Your invoices will no longer say where to pay you.')) return;
    setBankMsg(''); setError('');
    try {
      await api.delete('/api/profile/bank-details');
      setBank(null);
      setForm(BLANK);
      setEditingBank(false);
      setBankMsg('Bank details removed.');
    } catch (err) {
      setError(err.response?.data?.error || 'They could not be removed.');
    }
  };

  if (loading) return <div className="card"><p className="muted" style={{ margin: 0 }}>Loading your billing details…</p></div>;

  return (
    <div className="card">
      <h2>Invoicing</h2>
      <p className="muted">
        When the club bills a month, it raises an invoice in your name. This is what goes on it.
      </p>
      {error && <div className="error-msg prewrap">{error}</div>}

      <form onSubmit={saveAddress} className="billing-form">
        <div className="form-row">
          <label htmlFor="billing-address">Your address</label>
          <textarea
            id="billing-address"
            name="billing_address"
            rows={4}
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder={'14 Example Street\nPreston\nPR2 1AB'}
          />
          <p className="muted field-hint">One line each, the way you would write it on a letter.</p>
        </div>
        {addressMsg && <div className="success-msg">{addressMsg}</div>}
        <button className="btn" type="submit" disabled={address === savedAddress}>Save address</button>
      </form>

      <hr className="billing-rule" />

      <h3 className="billing-subhead">Where you are paid</h3>

      {!available ? (
        <>
          <p className="muted">
            Bank details cannot be stored yet. The club has to set an encryption key on the portal
            first, because these are never kept unencrypted. Nothing you type here would be saved,
            so the form is switched off rather than pretending to work.
          </p>
          {/* The actual reason, for whoever is fixing it. Harmless to a coach
              and saves a round trip through Daniel to find out what is wrong. */}
          {unavailableReason && <p className="muted field-hint">{unavailableReason}</p>}
        </>
      ) : bank && !editingBank ? (
        <>
          <table className="billing-table">
            <tbody>
              <tr><th>Name on the account</th><td>{bank.account_name}</td></tr>
              <tr><th>Sort code</th><td>{bank.sort_code}</td></tr>
              <tr><th>Account number</th><td>{bank.account_number}</td></tr>
            </tbody>
          </table>
          {bankMsg && <div className="success-msg">{bankMsg}</div>}
          <div className="panel-buttons">
            <button className="btn secondary" onClick={() => { setForm(BLANK); setEditingBank(true); }}>Change these</button>
            <button className="btn danger" onClick={removeBank}>Remove them</button>
          </div>
          <p className="muted field-hint" style={{ marginTop: '0.75rem' }}>
            Only you can see or change these. An admin sees the last four digits and nothing else,
            and the full account appears on your invoice so the club can pay it.
          </p>
        </>
      ) : (
        <form onSubmit={saveBank} className="billing-form">
          {bankMsg && <div className="success-msg">{bankMsg}</div>}
          <div className="form-row">
            <label htmlFor="bank-account-name">Name on the account</label>
            <input
              id="bank-account-name"
              name="account_name"
              value={form.account_name}
              onChange={e => setForm({ ...form, account_name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="bank-sort-code">Sort code</label>
            {/* inputMode="numeric" brings up the number pad on a phone, which
                is where most coaches will do this, while still allowing the
                dashes people naturally type. The server strips them. */}
            <input
              id="bank-sort-code"
              name="sort_code"
              inputMode="numeric"
              autoComplete="off"
              placeholder="40-47-84"
              value={form.sort_code}
              onChange={e => setForm({ ...form, sort_code: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="bank-account-number">Account number</label>
            <input
              id="bank-account-number"
              name="account_number"
              inputMode="numeric"
              autoComplete="off"
              placeholder="12345678"
              value={form.account_number}
              onChange={e => setForm({ ...form, account_number: e.target.value })}
              required
            />
          </div>
          <div className="panel-buttons">
            <button className="btn" type="submit">Save bank details</button>
            {bank && <button className="btn secondary" type="button" onClick={() => { setEditingBank(false); setForm(BLANK); }}>Cancel</button>}
          </div>
        </form>
      )}
    </div>
  );
};

export default BillingDetails;
